/**
 * Live player counts, shown on the Players tab and as a tile on the Overview page.
 *
 * Every value is rendered as a React text child, never as HTML. Refreshes every 15 seconds; React
 * Query does not poll while the tab is hidden. When a refresh fails the last good numbers stay on
 * screen, marked as stale, rather than being replaced by an error.
 */

import { useQuery } from "@tanstack/react-query";
import { fetchPlayerCounts } from "../api";

const REFRESH_MS = 15_000;

/** One query shared by every place that shows counts, so the page never polls twice. */
export function usePlayerCounts() {
  return useQuery({
    queryKey: ["player-counts"],
    queryFn: () => fetchPlayerCounts(),
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
  });
}

export function players(count: number): string {
  return `${count} ${count === 1 ? "player" : "players"}`;
}

export function PlayerCounts(): JSX.Element {
  const { data, isError } = usePlayerCounts();

  if (!data) {
    return (
      <section className="player-counts" aria-live="polite">
        <p className="player-counts-status">{isError ? "Unavailable right now" : "Loading…"}</p>
      </section>
    );
  }

  return (
    <section className="player-counts" aria-live="polite" data-stale={isError || undefined}>
      <div className="player-counts-total">{players(data.total)} online</div>
      {isError && <p className="player-counts-status">Unavailable right now — showing the last known numbers</p>}
      <ul className="player-counts-list">
        {data.proxies.map((proxy) => (
          <li key={proxy.id}>
            {players(proxy.players)} {proxy.players === 1 ? "is" : "are"} on the {proxy.label} proxy
          </li>
        ))}
      </ul>
    </section>
  );
}
